import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { Button, List, message, Popconfirm, Tooltip } from "antd";
import React from "react";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "../../../redux/hooks.ts";
import {
  downloadRequirementFile,
  removeRequirementFile,
} from "../../../redux/actions/requirementsActions.tsx";
import {
  File,
  Folder,
  Requirement,
  Status,
} from "../../../redux/interfaces/folder.types.ts";
import { FilePreview } from "./FilePreview.tsx";
import { lightBlue, red } from "../../../utils/theme/colors.ts";
import { useLocation } from "react-router-dom";
import { parseDateToSpanishString } from "../../../utils/common/parseDateToSpanishString.ts";
import api from "../../../api";
import { extractErrorMessage } from "../../../utils/extractErrorMessage.ts";
import {
  applyFileDeletionResponse,
  replaceRequirementInFolder,
} from "../../../utils/vault/fileDeletion.ts";
import { setActiveRequirement as setAdminActiveRequirement } from "../../../redux/slices/requirementsSlice.ts";
import {
  setActiveRequirement as setVaultActiveRequirement,
  setActiveVault,
} from "../../../redux/slices/vaultSlice.ts";
import { setActiveFolder } from "../../../redux/slices/folderSlice.ts";

interface Props {
  file: File;
  download: boolean;
  disableDelete?: boolean;
}

const validDeleteStatus: any[] = [Status.PendingUpload, Status.Invalid];

export const FileComponent: React.FC<Props> = ({
  file,
  download,
  disableDelete = false,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [downloadId, setDownloadId] = React.useState<string | null>(null);
  const [previewFile, setPreviewFile] = React.useState<boolean>(false);
  const location = useLocation();

  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { loadingDownloadFile } = useAppSelector((state) => state.requirements);
  const { activeRequirement: activeVaultRequirement } = useAppSelector(
    (state) => state.vault,
  );

  const { userInfo } = useAppSelector((state) => state.auth);

  const showDelete =
    location.pathname.includes("folder") && userInfo !== null
      ? true
      : validDeleteStatus.includes(activeVaultRequirement?.status);

  const createdAt = file.created_at;

  const onRemoveFile = async () => {
    if (disableDelete) return;

    let deletionResponse;
    try {
      deletionResponse = await dispatch(removeRequirementFile(file)).unwrap();
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo eliminar el archivo."),
      );
      return;
    }

    // Read current state after the network request. The operator may have
    // navigated elsewhere while DELETE was in flight; never restore a stale
    // requirement or folder selection in that case.
    const currentState = store.getState();
    const currentAdminRequirement = currentState.requirements.activeRequirement;
    const currentVaultRequirement = currentState.vault.activeRequirement;
    const responseRequirement = deletionResponse.requirement;
    const matchesDeletedFile = (requirement: Requirement | null | undefined) =>
      requirement?.files?.some(
        (requirementFile) => requirementFile.id === file.id,
      ) ?? false;
    const adminRequirementMatches = responseRequirement
      ? currentAdminRequirement?.id === responseRequirement.id
      : matchesDeletedFile(currentAdminRequirement);
    const vaultRequirementMatches = responseRequirement
      ? currentVaultRequirement?.id === responseRequirement.id
      : matchesDeletedFile(currentVaultRequirement);
    const updatedAdminRequirement = adminRequirementMatches
      ? applyFileDeletionResponse(
          currentAdminRequirement!,
          deletionResponse,
          file.id,
        )
      : undefined;
    const updatedVaultRequirement = vaultRequirementMatches
      ? applyFileDeletionResponse(
          currentVaultRequirement!,
          deletionResponse,
          file.id,
        )
      : undefined;

    if (updatedAdminRequirement) {
      dispatch(setAdminActiveRequirement(updatedAdminRequirement));
    }
    if (updatedVaultRequirement) {
      dispatch(setVaultActiveRequirement(updatedVaultRequirement));
    }

    const updatedRequirement =
      responseRequirement ?? updatedAdminRequirement ?? updatedVaultRequirement;

    const currentAdminFolder = currentState.folder.activeFolder;
    if (currentAdminFolder && updatedRequirement) {
      dispatch(
        setActiveFolder(
          replaceRequirementInFolder(currentAdminFolder, updatedRequirement),
        ),
      );
    }
    const currentVault = currentState.vault.activeVault;
    if (currentVault && updatedRequirement) {
      dispatch(
        setActiveVault(
          replaceRequirementInFolder(currentVault, updatedRequirement),
        ),
      );
    }

    // Reconcile folder summaries/status without using thunks whose fulfilled
    // reducers can replace a newer selection. Each response is applied only if
    // that same folder is still active when the request finishes.
    const folderIds = new Set(
      [currentAdminFolder?.id, currentVault?.id].filter(
        (folderId): folderId is string => Boolean(folderId),
      ),
    );
    const refreshedFolders = await Promise.allSettled(
      [...folderIds].map((folderId) =>
        api
          .get<Folder>(`/docvault/folders/${folderId}`)
          .then((response) => response.data),
      ),
    );
    for (const result of refreshedFolders) {
      if (result.status !== "fulfilled") continue;
      const latestState = store.getState();
      if (latestState.folder.activeFolder?.id === result.value.id) {
        dispatch(setActiveFolder(result.value));
      }
      if (latestState.vault.activeVault?.id === result.value.id) {
        dispatch(setActiveVault(result.value));
      }
    }
  };

  const onDownloadFile = async () => {
    setDownloadId(file.id);
    try {
      await dispatch(downloadRequirementFile(file)).unwrap();
      messageApi.open({
        type: "success",
        content: "Archivo descargado.",
      });
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo descargar el archivo."),
      );
    } finally {
      setDownloadId(null);
    }
  };

  const onViewFile = async () => {
    setDownloadId(file.id);
    try {
      const response = await api.get(
        `/docvault/requirements/document-file/${file.id}/download/`,
        { responseType: "blob" },
      );

      const fileExtension = file.filename.split(".").pop()?.toLowerCase();
      let mimeType = "application/octet-stream"; // default

      const mimeTypes: Record<string, string> = {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        webp: "image/webp",
        pdf: "application/pdf",
      };

      if (
        fileExtension &&
        Object.prototype.hasOwnProperty.call(mimeTypes, fileExtension)
      ) {
        mimeType = mimeTypes[fileExtension];
      }

      const blob = new Blob([response.data], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, "_blank");

      // If window was blocked
      if (!newWindow) {
        messageApi.warning(
          "El navegador bloqueó la apertura. Por favor, permite las ventanas emergentes.",
        );
      }

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);

      messageApi.open({
        type: "success",
        content: "Archivo abierto en nueva pestaña.",
      });
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo abrir el archivo."),
      );
    } finally {
      setDownloadId(null);
    }
  };

  return (
    <List.Item
      key={file.id}
      className="px-4 py-2 flex items-center justify-between transition-colors duration-300 ease-in-out hover:bg-gray-100"
      style={{ border: "none", boxShadow: "none" }}
    >
      {contextHolder}
      <FilePreview
        file={file}
        opened={previewFile}
        handleOpen={setPreviewFile}
      />
      <div className="flex items-center">
        <PaperClipOutlined className="text-blue-500 mr-2" />
        <div>
          <div
            className="text-sm block cursor-pointer hover:text-blue-600"
            onClick={() => setPreviewFile(true)}
          >
            {file.filename}
          </div>
          {!!createdAt && (
            <span className="text-xs">
              <b>
                Archivo subido el{" "}
                {parseDateToSpanishString(file.created_at)}{" "}
              </b>
            </span>
          )}
        </div>
      </div>
      <div>
        {download && (
          <Tooltip title="Abrir archivo">
            <Button
              type="text"
              icon={
                loadingDownloadFile && downloadId === file.id ? (
                  <EyeOutlined />
                ) : (
                  <EyeOutlined style={{ color: lightBlue }} />
                )
              }
              onClick={onViewFile}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
        )}
        {download && (
          <Tooltip title="Descargar archivo">
            <Button
              type="text"
              icon={
                loadingDownloadFile && downloadId === file.id ? (
                  <LoadingOutlined />
                ) : (
                  <DownloadOutlined style={{ color: lightBlue }} />
                )
              }
              onClick={onDownloadFile}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
        )}
        {showDelete && (
          <Popconfirm
            title="Eliminar archivo"
            description="¿Estás seguro que deseas continuar?"
            onConfirm={onRemoveFile}
            okText="Continuar"
            cancelText="Cancelar"
          >
            <Tooltip title="Eliminar archivo" className="cursor-pointer">
              <Button
                type="text"
                disabled={disableDelete}
                icon={<DeleteOutlined style={{ color: red }} />}
                className="text-red-500 hover:text-red-700"
              />
            </Tooltip>
          </Popconfirm>
        )}
      </div>
    </List.Item>
  );
};
